import { Button, IconTile, TextTooltip } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { FirstTransactionFetchNotice as FirstTransactionFetchNoticeState } from '@query'
import { showFeaturebase, useScreen } from '@utils'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaComments, FaQuestionCircle } from 'react-icons/fa'
import styled, { useTheme } from 'styled-components'
import { useFirstTransactionFetchNotice } from './useFirstTransactionFetchNotice'

export const FirstTransactionFetchNoticeBanner: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { isMobile } = useScreen()
  const { notice } = useFirstTransactionFetchNotice()

  const isVisible = notice !== FirstTransactionFetchNoticeState.Idle
  const isDelayed = notice === FirstTransactionFetchNoticeState.Delayed
  const title = t(
    isDelayed ? 'document.firstBankFetch.delayedTitle' : 'document.firstBankFetch.title'
  )
  const description = t(
    isDelayed ? 'document.firstBankFetch.delayedBody' : 'document.firstBankFetch.body'
  )
  const spinnerSize = isMobile ? 18 : 20

  return (
    <AnimatePresence>
      {isVisible && (
        <NoticeMotion
          key="first-transaction-fetch-notice"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ type: 'tween', duration: 0.2 }}
        >
          <Card role="status">
            <Row $compact={isMobile} $alignStart={isDelayed}>
              <SpinnerTile
                $compact={isMobile}
                color={theme.dashboard.infoTint}
                iconColor={theme.colors.primary}
                icon={<Spinner size={spinnerSize} color={theme.colors.primary} />}
              />
              <Copy>
                <TextBlock>
                  <TitleRow $compact={isMobile && !isDelayed}>
                    <Title>{title}</Title>
                    {isMobile && (
                      <TextTooltip tooltip={description} placement="bottom" openOnClick>
                        <HelpIcon aria-label={description} />
                      </TextTooltip>
                    )}
                  </TitleRow>
                  {!isMobile && <Description>{description}</Description>}
                </TextBlock>
                {isDelayed && (
                  <Button
                    type="button"
                    size="sm"
                    icon={<FaComments />}
                    onClick={() => showFeaturebase('messages')}
                  >
                    {t('document.firstBankFetch.askInChat')}
                  </Button>
                )}
              </Copy>
            </Row>
          </Card>
        </NoticeMotion>
      )}
    </AnimatePresence>
  )
}

const NoticeMotion = styled(motion.div)`
  overflow: hidden;
  flex-shrink: 0;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Row = styled.div<{ $compact?: boolean; $alignStart?: boolean }>`
  display: flex;
  align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
  gap: ${({ theme, $compact }) => ($compact ? theme.spacing.sm : theme.spacing.md)}rem;
`

const SpinnerTile = styled(IconTile)<{ $compact?: boolean }>`
  width: ${({ $compact }) => ($compact ? 36 : 40)}px;
  height: ${({ $compact }) => ($compact ? 36 : 40)}px;
  border-radius: 0.6rem;

  svg {
    width: ${({ $compact }) => ($compact ? 18 : 20)}px;
    height: ${({ $compact }) => ($compact ? 18 : 20)}px;
  }
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex: 1;
  min-width: 0;
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
`

const TitleRow = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  width: 100%;
  height: ${({ $compact }) => ($compact ? '36px' : 'auto')};
`

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  line-height: 1.25;
  min-width: 0;
`

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;
`

const HelpIcon = styled(FaQuestionCircle)`
  display: block;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  cursor: pointer;
`
